import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  ShieldAlert,
  AlertCircle,
  Plus,
  Edit,
  Trash2,
  Eye,
  RefreshCw,
  MoreHorizontal,
  HelpCircle,
  Layers
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast, ToastContainer } from '../common/Toast';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import DataTable from '../common/DataTable';
import ConfirmationModal from '../common/ConfirmationModal';
import PreContractService from '../../services/preContractService';

/* =========================
   Action Dropdown
========================= */
const PolicyActionDropdown = ({ policy, onView, onEdit, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isOpen]);

  const dropdownMenu =
    isOpen && buttonRef.current
      ? createPortal(
          <ul
            className="dropdown-menu dropdown-menu-end show"
            style={{
              position: 'fixed',
              top: `${buttonRef.current.getBoundingClientRect().bottom + 8}px`,
              right: `${
                window.innerWidth -
                buttonRef.current.getBoundingClientRect().right
              }px`,
              zIndex: 999999,
              minWidth: '180px',
              backgroundColor: '#fff',
              listStyle: 'none',
              padding: 0
            }}
          >
            <li>
              <button
                className="dropdown-item d-flex align-items-center"
                onClick={() => {
                  setIsOpen(false);
                  onView(policy);
                }}
              >
                <Eye size={16} className="me-2 text-muted" /> View
              </button>
            </li>
            <li>
              <button
                className="dropdown-item d-flex align-items-center"
                onClick={() => {
                  setIsOpen(false);
                  onEdit(policy);
                }}
              >
                <Edit size={16} className="me-2 text-muted" /> Edit
              </button>
            </li>
            <li className="border-top">
              <button
                className="dropdown-item d-flex align-items-center text-danger"
                onClick={() => {
                  setIsOpen(false);
                  onDelete(policy);
                }}
              >
                <Trash2 size={16} className="me-2" /> Delete
              </button>
            </li>
          </ul>,
          document.body
        )
      : null;

  return (
    <>
      <div ref={dropdownRef}>
        <button
          ref={buttonRef}
          className="inline-flex items-center justify-center w-8 h-8 border bg-white rounded shadow-sm"
          onClick={() => setIsOpen(!isOpen)}
        >
          <MoreHorizontal size={16} />
        </button>
      </div>
      {dropdownMenu}
    </>
  );
};

/* =========================
   Main Component
========================= */
const PreContractPolicies = () => {
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [policies, setPolicies] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [policyToDelete, setPolicyToDelete] = useState(null);

  const loadPolicies = async () => {
    setLoading(true);
    try {
      const response = await PreContractService.getPolicies();
      setPolicies(response?.data || []);
    } catch (err) {
      showError('Failed to load policies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPolicies();
  }, []);

  const handleDeleteConfirm = async () => {
    try {
      if (policyToDelete?._id) {
        await PreContractService.deletePolicy(policyToDelete._id);
        success('Policy deleted successfully');
        loadPolicies();
      }
    } catch (err) {
      showError('Failed to delete policy');
    } finally {
      setShowDeleteModal(false);
      setPolicyToDelete(null);
    }
  };

  const headerActions = [
    {
      label: '',
      icon: <HelpCircle size={18} />,
      onClick: () => {},
      variant: 'outline'
    },
    {
      label: '',
      icon: <RefreshCw size={18} />,
      onClick: loadPolicies,
      variant: 'outline'
    },
    {
      label: 'New Policy',
      icon: <Plus size={18} />,
      onClick: () =>
        navigate('/contracts/pre-contract/policies/create'),
      variant: 'primary'
    }
  ];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="Compliance Policies"
        description="Manage pre-contract validation rules"
        actions={headerActions}
      />

      <div className="page-container">
        <DataTable
          showActions={false}
          data={policies}
          loading={loading}
          emptyMessage="No policies found. Create one to get started."
          columns={[
            {
              key: 'name',
              label: 'Policy',
              render: (value, row) => (
                <div>
                  <div className="fw-bold">{row.name}</div>
                  <div className="text-muted small">
                    {row.description || 'No description provided'}
                  </div>
                </div>
              )
            },
            {
              key: 'type',
              label: 'Rule Type',
              render: (value) => (
                <span className="badge bg-light text-dark border">
                  <Layers size={14} className="me-1" />
                  {value}
                </span>
              )
            },
            {
              key: 'thresholdValue',
              label: 'Condition',
              render: (value, row) => (
                <span>
                  {row.type === 'Value Threshold' && row.currency
                    ? `${row.currency} `
                    : ''}
                  {value}
                </span>
              )
            },
            {
              key: 'enforcement',
              label: 'Enforcement',
              render: (value) => (
                <div
                  className={`priority-badge ${
                    value === 'Critical Block'
                      ? 'critical'
                      : 'medium'
                  }`}
                >
                  {value === 'Critical Block' ? (
                    <ShieldAlert size={14} className="me-1" />
                  ) : (
                    <AlertCircle size={14} className="me-1" />
                  )}
                  {value}
                </div>
              )
            },
            {
              key: 'isActive',
              label: 'Status',
              render: (value) => (
                <span
                  className={`badge ${
                    value ? 'bg-success' : 'bg-secondary'
                  }`}
                >
                  {value ? 'Active' : 'Inactive'}
                </span>
              )
            },
            {
              key: 'actions',
              label: 'Actions',
              width: '80px',
              render: (_, row) => (
                <PolicyActionDropdown
                  policy={row}
                  onView={(p) =>
                    navigate(
                      `/contracts/pre-contract/policies/view/${p._id}`
                    )
                  }
                  onEdit={(p) =>
                    navigate(
                      `/contracts/pre-contract/policies/edit/${p._id}`
                    )
                  }
                  onDelete={(p) => {
                    setPolicyToDelete(p);
                    setShowDeleteModal(true);
                  }}
                />
              )
            }
          ]}
        />
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Policy"
        message={`Delete "${policyToDelete?.name}"?`}
        confirmText="Delete"
        confirmVariant="danger"
      />

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default PreContractPolicies;
